import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { GroceryCategory, CATEGORY_METADATA } from '../../types/groceryList';
import { Paprika } from '../../styles/theme';

interface AddCustomItemModalProps {
  visible: boolean;
  onClose: () => void;
  onAdd: (name: string, category: GroceryCategory) => void;
}

const AddCustomItemModal: React.FC<AddCustomItemModalProps> = ({
  visible,
  onClose,
  onAdd,
}) => {
  const [itemName, setItemName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<GroceryCategory>(
    GroceryCategory.Other
  );
  const { t } = useTranslation();

  const handleAdd = () => {
    if (itemName.trim()) {
      onAdd(itemName.trim(), selectedCategory);
      setItemName('');
      setSelectedCategory(GroceryCategory.Other);
      onClose();
    }
  };

  const handleCancel = () => {
    setItemName('');
    setSelectedCategory(GroceryCategory.Other);
    onClose();
  };

  // Get categories sorted by sortOrder
  const categories = Object.entries(CATEGORY_METADATA)
    .map(([key, meta]) => ({
      key: key as GroceryCategory,
      ...meta,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleCancel}
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={handleCancel}
      >
        <TouchableOpacity
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
          style={styles.modalContent}
        >
          <View style={styles.header}>
            <Text style={styles.title}>{t('groceryList.addCustomItemTitle')}</Text>
          </View>

          <View style={styles.form}>
            {/* Item Name Input */}
            <Text style={styles.label}>{t('groceryList.itemName')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('groceryList.itemNamePlaceholder')}
              value={itemName}
              onChangeText={setItemName}
              autoFocus
              returnKeyType="done"
              onSubmitEditing={handleAdd}
            />

            {/* Category Picker */}
            <Text style={[styles.label, styles.categoryLabel]}>{t('groceryList.category')}</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.categoryScroll}
              contentContainerStyle={styles.categoryScrollContent}
            >
              {categories.map((cat) => (
                <TouchableOpacity
                  key={cat.key}
                  style={[
                    styles.categoryButton,
                    selectedCategory === cat.key && styles.categoryButtonSelected,
                    {
                      borderColor: cat.color,
                      backgroundColor:
                        selectedCategory === cat.key ? cat.color + '20' : 'white',
                    },
                  ]}
                  onPress={() => setSelectedCategory(cat.key)}
                >
                  <Text style={styles.categoryIcon}>{cat.icon}</Text>
                  <Text
                    style={[
                      styles.categoryButtonText,
                      selectedCategory === cat.key && {
                        color: cat.color,
                        fontWeight: '700',
                      },
                    ]}
                  >
                    {t(cat.labelKey)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Action Buttons */}
          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={handleCancel}
            >
              <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.button,
                styles.addButton,
                !itemName.trim() && styles.addButtonDisabled,
              ]}
              onPress={handleAdd}
              disabled={!itemName.trim()}
            >
              <Text
                style={[
                  styles.addButtonText,
                  !itemName.trim() && styles.addButtonTextDisabled,
                ]}
              >
                {t('groceryList.addItemButton')}
              </Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    ...Platform.select({
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.border,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: Paprika.textPrimary,
  },
  form: {
    padding: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Paprika.textPrimary,
    marginBottom: 8,
  },
  categoryLabel: {
    marginTop: 16,
  },
  input: {
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: Paprika.textPrimary,
  },
  categoryScroll: {
    marginBottom: 8,
  },
  categoryScrollContent: {
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 2,
    marginRight: 8,
  },
  categoryButtonSelected: {
    borderWidth: 2,
  },
  categoryIcon: {
    fontSize: 20,
    marginRight: 6,
  },
  categoryButtonText: {
    fontSize: 13,
    color: Paprika.textPrimary,
    fontWeight: '500',
  },
  actions: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
  },
  button: {
    flex: 1,
    marginHorizontal: 6,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: Paprika.background,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Paprika.textSecondary,
  },
  addButton: {
    backgroundColor: Paprika.primary,
  },
  addButtonDisabled: {
    backgroundColor: Paprika.border,
  },
  addButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  addButtonTextDisabled: {
    color: Paprika.textSecondary,
  },
});

export default AddCustomItemModal;
